/**
 * ImpersonationBanner — persistent Coral non-dismissible banner shown on
 * EVERY customer-facing page when the current session is an impersonation.
 *
 * Per spec §P1.4: "You are impersonating [Admin Name] of [Tenant Name].
 * Session ends in [X minutes]. End impersonation now →"
 *
 * Implementation: reads sessionStorage via impersonation-client.js; if a
 * meta object is present and not expired, renders the banner with a live
 * countdown. End-now button POSTs to /api/admin/impersonation-end with
 * the impersonation token as bearer (the endpoint dispatches on the
 * customer_tab_banner path), then clears sessionStorage + reloads to a
 * non-impersonated state.
 */

import { useEffect, useState } from "react";
import {
  getImpersonationToken,
  getImpersonationMeta,
  clearImpersonation,
} from "../lib/impersonation-client.js";

export default function ImpersonationBanner() {
  const [meta, setMeta] = useState(() => getImpersonationMeta());
  const [now, setNow] = useState(() => Date.now());
  const [ending, setEnding] = useState(false);

  // Countdown tick.
  useEffect(() => {
    if (!meta) return;
    const handle = setInterval(() => setNow(Date.now()), 30 * 1000);
    return () => clearInterval(handle);
  }, [meta]);

  if (!meta) return null;

  const expMs = (meta.session_expires_at || meta.exp || 0) * 1000;
  const remainingMs = expMs - now;
  if (remainingMs <= 0) {
    // Session expired client-side; clear silently. Server will reject
    // any subsequent API calls with the expired token.
    clearImpersonation();
    return null;
  }
  const remainingMinutes = Math.max(1, Math.ceil(remainingMs / 60_000));

  const endNow = async () => {
    if (ending) return;
    setEnding(true);
    try {
      const token = getImpersonationToken();
      if (token) {
        await fetch("/.netlify/functions/admin-impersonation-end", {
          method: "POST",
          headers: {
            "content-type": "application/json",
            authorization: `Bearer ${token}`,
          },
          body: "{}",
        });
      }
    } catch {
      // Best-effort — even if the audit row fails to write, clear the
      // sessionStorage so the impersonator's subsequent requests fall
      // back to their normal Supabase session.
    } finally {
      clearImpersonation();
      // Hard reload to drop any cached impersonated state in React.
      if (typeof window !== "undefined") window.location.reload();
    }
  };

  return (
    <div
      role="alert"
      data-testid="impersonation-banner"
      style={{
        position: "sticky",
        top: 0,
        zIndex: 999,
        background: "var(--ppr-color-accent-coral)",
        color: "var(--ppr-color-background-page)",
        padding: "10px 24px",
        display: "flex",
        alignItems: "center",
        gap: 12,
        fontFamily: "var(--ppr-typography-font-family-display)",
        fontSize: "var(--ppr-typography-font-size-sm)",
        letterSpacing: "var(--ppr-typography-letter-spacing-wide)",
      }}
    >
      <span style={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: "var(--ppr-typography-letter-spacing-wider)" }}>Impersonation active</span>
      <span style={{ flex: 1 }}>
        You are acting as the target user.
        Session ends in <strong>{remainingMinutes} minute{remainingMinutes === 1 ? "" : "s"}</strong>.
        Reason: <strong>{meta.impersonation_reason_category || "—"}</strong>
        {meta.impersonation_reason_detail ? ` — ${meta.impersonation_reason_detail}` : ""}
      </span>
      <button
        type="button"
        onClick={endNow}
        disabled={ending}
        data-testid="impersonation-banner-end"
        style={{
          padding: "6px 14px",
          background: "var(--ppr-color-background-page)",
          color: "var(--ppr-color-accent-coral)",
          border: "1.5px solid var(--ppr-color-background-page)",
          fontFamily: "var(--ppr-typography-font-family-display)",
          fontSize: "var(--ppr-typography-font-size-xs)",
          letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
          textTransform: "uppercase",
          cursor: ending ? "wait" : "pointer",
          opacity: ending ? 0.6 : 1,
        }}
      >
        {ending ? "Ending…" : "End impersonation now →"}
      </button>
    </div>
  );
}
